import { getFormUrl } from './getFormUrl';

/** Mirror of the 255-char cap the `campaignDecided` Optimizely listener
 * enforces on the real `experiment_exposures` cookie. Keep in sync with the
 * listener so the piggybacked value trims identically. */
const EXPERIMENT_EXPOSURES_MAX_LENGTH = 255;

/** Trailing terminator that makes the `formUrl` entry self-delimiting. Because
 * `encodeURIComponent` never emits `;`, it cannot appear inside the encoded
 * value, so a plain substring/"contains" match on `formUrl:<value>;` isolates
 * exactly one page in downstream tools (e.g. Power BI, which has no "ends-with"
 * filter). Without it the homepage `formUrl:host%2F` is a prefix of every other
 * page (`...%2Fabout`), and trailing-slash pages also end in `%2F`, so neither
 * "contains" nor "ends-with %2F" can single out the homepage. */
const FORM_URL_TERMINATOR = ';';

/**
 * Returns a copy of the `experiment_exposures` value with a `formUrl:<value>;`
 * marker appended, for the outgoing Pardot payload ONLY. The real cookie is
 * never touched — it stays the shared source of truth for FullStory's Custom
 * Event Relay tag, potential future GA4 use, and the Optimizely listener
 * itself (which may still append exposures during the same session). Call this
 * at lead-submit time, NOT inside `getOptimizelyData()` (a shared cookie read).
 *
 * The marker value is `encodeURIComponent(getFormUrl())` bounded by a
 * literal `formUrl:` prefix and a `;` terminator, so it can never contain the
 * comma or colon that delimit this field and is unambiguously bounded on both
 * sides. A future parser should match the entry by its literal `formUrl:` prefix
 * (experiment/variation IDs are numeric today, but that is not guaranteed
 * long-term), strip the trailing `;`, then `decodeURIComponent` the value.
 *
 * Trimming mirrors the listener: while the joined string exceeds 255 chars and
 * more than one entry remains, drop the OLDEST (front) entry. `formUrl` is
 * appended last, so it survives eviction while real — but older — experiment
 * exposures may legitimately be dropped to fit under the cap. That is an
 * accepted tradeoff of piggybacking on this field, not a bug.
 *
 * FAIL-SAFE: this runs at the sensitive lead-submit moment, so it must never
 * throw or change the submission outcome. On the server (no `window`) or on
 * ANY unexpected error (a sandboxed `window.location`, an `encodeURIComponent`
 * URIError on a lone surrogate, etc.) it aborts and returns the original
 * `exposures` value unchanged — exactly what the form would have sent without
 * this feature.
 */
export const appendFormUrlToExposures = (exposures: string | null): string | null => {
    try {
        if (typeof window === 'undefined' || !window.location) {
            return exposures;
        }
        // getFormUrl re-checks the above and returns null on any failure of its
        // own; the guard above stays so the SSR bail is visible here, and so an
        // unexpected error still reaches this function's catch and its warning.
        const formUrl = getFormUrl();
        if (formUrl === null) {
            return exposures;
        }
        const formUrlEntry = `formUrl:${encodeURIComponent(formUrl)}${FORM_URL_TERMINATOR}`;
        const entries = exposures ? exposures.split(',') : [];
        entries.push(formUrlEntry);

        let combined = entries.join(',');
        while (combined.length > EXPERIMENT_EXPOSURES_MAX_LENGTH && entries.length > 1) {
            entries.shift();
            combined = entries.join(',');
        }
        // If the marker alone still does not fit, returning it would mean sending an
        // over-cap value carrying no exposures at all — and Salesforce truncating at
        // 255 can cut mid-percent-escape, leaving something decodeURIComponent
        // rejects. A missing marker is recoverable; a corrupt field is not. Fall back
        // to the untouched value, per the fail-safe contract above.
        if (combined.length > EXPERIMENT_EXPOSURES_MAX_LENGTH) {
            return exposures;
        }
        return combined;
    } catch (error) {
        // Never surface — the lead submit proceeds with the untouched value.
        // The log is itself guarded so it can never prevent the fallback return.
        try {
            console.warn('[appendFormUrlToExposures] fell back to raw experiment_exposures:', error);
        } catch {
            /* no-op */
        }
        return exposures;
    }
};
